import React, { ReactNode, useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ResidentList } from '../../../../components/society/ResidentList'
import { SpinnerWrapper } from '../../../../components/common/SpinnerWrapper'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { unitQueries } from '../../../../api/units'
import { userQueries } from '../../../../api/users'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { getUserFullName } from '../../../../helpers/user'
import { IUnitModel } from '../../../../interfaces/models/units.interfaces'

const getUniqueUserIds = (units?: IUnitModel[]): string[] => {
  const userIds =
    units
      ?.flatMap((unit) => unit.residentsList || [])
      .map((resident) => resident.userId) || []

  const uniqueUserIds = new Set<string>(userIds)
  return [...uniqueUserIds]
}

export const ResidentListView = (): ReactNode => {
  const [searchString, setSearchString] = useState('')
  const { translate } = useAppTranslation()
  const { society, isLoading: isFetchingSociety } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('society:resident-list', {
      '{{ societyName }}': society?.name,
    })
  )
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )
  const uniqueUserIds = getUniqueUserIds(societyUnits)

  const residentsQuery = useQueries({
    queries:
      [...uniqueUserIds].map((userId) => {
        return {
          ...userQueries.getUser(userId),
          select: (user: IUserModel) => {
            return {
              user: { ...user, fullName: getUserFullName(user) },
              units: societyUnits?.filter((unit) =>
                unit.residentsList?.find(
                  (resident) => resident.userId === userId
                )
              ),
            }
          },
        }
      }) || [],
  })

  const residentUsers = residentsQuery.flatMap((query) => query.data || [])
  const isFetchingUsers = residentsQuery.find(
    (query) => query.isLoading === true
  )

  const getFilteredUsers = useCallback(() => {
    const options = {
      threshold: 0.2,
      keys: [
        'user.fullName',
        'user.name',
        'user.surname',
        'units.title',
        'units.titleLegal',
      ],
    }
    const fuse = new Fuse(residentUsers, options)

    const filteredUsers =
      searchString === ''
        ? residentUsers
        : fuse.search(searchString).map((result) => result.item)

    return filteredUsers.map((_user) => _user.user)
  }, [residentUsers, searchString])

  const onChangeSearchString = (
    event: React.FormEvent<HTMLInputElement>
  ): void => {
    setSearchString(event.currentTarget.value)
  }

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('residentListView.title')}
        searchBar={{ value: searchString, onChange: onChangeSearchString }}
      >
        {isFetchingSociety || isFetchingUsers ? (
          <SpinnerWrapper />
        ) : (
          <ResidentList users={getFilteredUsers()} society={society} />
        )}
      </ViewBase>
    </ErrorBoundary>
  )
}
